/**
 * Keywords Strategy - Simple keyword collection and management
 * 
 * This strategy handles keyword requirements by:
 * 1. Showing extracted keywords from JD/requirements using MultiRowSelectorTemplate
 * 2. Allowing users to add more keywords via chat
 * 3. Taking user input as-is and adding as OR conditions (no processing)
 */

import { createBaseStrategy, StrategyPhase } from './baseStrategy';
import centralDiscoveryQueue from '../centralDiscoveryQueue';
import { DISCOVERY_TEMPLATE_TYPES } from '../discoveryQueueSchema';
import { handleAgentSelection } from '../agenticHelper';

/**
 * Extract keywords from requirements
 * @param {Array} requirements - The ICP requirements with keywords section
 * @returns {Array} Array of keyword strings
 */
const extractKeywordsFromRequirements = (requirements) => {
  const keywords = [];
  
  requirements.forEach(req => {
    if (req.section_name === "Keywords" && req.bool_str) {
      // Parse bool_str to extract keywords - split by OR and clean up
      const keywordText = req.bool_str
        .replace(/\(|\)/g, '') // Remove parentheses
        .split(' OR ') // Split by OR
        .map(keyword => keyword.trim())
        .filter(keyword => keyword.length > 0);
      
      keywords.push(...keywordText);
    }
  });
  
  return [...new Set(keywords)]; // Remove duplicates
};

/**
 * Extract user-added keywords from chat messages
 * @param {Array} messages - Chat messages
 * @returns {Array} Array of user-added keywords
 */
const extractUserAddedKeywords = (messages) => {
  const userKeywords = [];
  
  // Look for messages after the initial keywords were shown
  // Simple heuristic: take user messages that mention adding keywords
  messages.forEach(msg => {
    if (msg.role === 'User' && msg.content) {
      const content = msg.content.toLowerCase();
      if (content.includes('add') || content.includes('include') || content.includes('also')) {
        // Extract potential keywords - split by common delimiters
        const potentialKeywords = msg.content
          .split(/[,\n\r]+/) // Split by commas or line breaks
          .map(k => k.trim())
          .filter(k => k.length > 2 && k.length < 50) // Reasonable keyword length
          .filter(k => !k.toLowerCase().includes('add') && !k.toLowerCase().includes('include')); // Filter out instruction words
        
        userKeywords.push(...potentialKeywords);
      }
    }
  });
  
  return [...new Set(userKeywords)]; // Remove duplicates
};

/**
 * Main keywords strategy function
 * @param {Object} decision - Current decision object
 * @param {Array} messages - Current conversation messages
 * @param {Function} setMessages - Function to update messages state
 * @returns {Object} Updated decision object
 */
const keywordsStrategy = async (decision, messages, setMessages) => {
  console.log('🎯🎯🎯 KEYWORDS STRATEGY CALLED!!! - Processing keywords topic');
  console.log('🎯 KEYWORDS STRATEGY - ENTRY TIMESTAMP:', new Date().toISOString());
  console.log('🎯 KEYWORDS STRATEGY - decision object:', decision);
  console.log('🎯 KEYWORDS STRATEGY - messages length:', messages?.length);
  console.log('🎯 KEYWORDS STRATEGY - setMessages available:', !!setMessages);
  console.log('🎯 KEYWORDS STRATEGY - This proves the strategy is being executed!');
  
  // Update thinking accordion to show processing
  if (setMessages) {
    setMessages(prev => {
      const updatedMessages = prev.map(msg =>
        msg.role === 'AI' && msg.content === 'Typing...'
          ? {
              ...msg,
              thinking: `${msg.thinking || ''}\nProcessing keywords discovery...`
            }
          : msg
      );
      return updatedMessages;
    });
  }
  
  try {
    // Get existing requirements
    const existingReqs = decision.getAllRequirements ? decision.getAllRequirements() : [];
    const keywordRequirements = existingReqs.filter(req => req.section_name === "Keywords");
    
    // Extract keywords from requirements
    const extractedKeywords = extractKeywordsFromRequirements(keywordRequirements);
    
    // Extract user-added keywords from chat
    const userAddedKeywords = extractUserAddedKeywords(messages);
    
    // Combine all keywords
    const allKeywords = [...new Set([...extractedKeywords, ...userAddedKeywords])];
    
    console.log('🎯 KEYWORDS STRATEGY:', {
      extractedKeywords: extractedKeywords.length,
      userAddedKeywords: userAddedKeywords.length,
      totalKeywords: allKeywords.length,
      keywords: allKeywords
    });
    
    // Check if we have any keywords to show
    if (allKeywords.length > 0) {
      // Update thinking accordion
      if (setMessages) {
        setMessages(prev => {
          const updatedMessages = prev.map(msg =>
            msg.role === 'AI' && msg.content === 'Typing...'
              ? {
                  ...msg,
                  thinking: `${msg.thinking || ''}\nFound ${allKeywords.length} keywords for review`
                }
              : msg
          );
          return updatedMessages;
        });
      }
      
      // Remove "Typing..." message
      if (setMessages) {
        setMessages(prev => prev.filter(msg => msg.content !== 'Typing...'));
      }
      
      // Show keywords selection widget
      return {
        ...decision,
        actionType: 'RENDER_KEYWORDS_SELECTION',
        componentProps: {
          keywords: allKeywords,
          extractedKeywords, // For display purposes
          userAddedKeywords, // For display purposes  
          setMessages,
          advanceToNextTopic: decision.advanceToNextTopic,
          onSendText: decision.onSendText,
          messages,
          onKeywordsConfirmed: (confirmedKeywords) => {
            console.log('Keywords confirmed:', confirmedKeywords);
            
            // Add confirmation message
            if (setMessages) {
              setMessages(prev => [...prev, {
                role: 'AI',
                content: `Perfect! I've noted these keywords for your search: ${confirmedKeywords.join(', ')}. You can always add more keywords by mentioning them in our chat.`,
                id: `ai_keywords_confirmed_${Date.now()}`
              }]);
            }
            
            // Advance to next topic
            if (decision.advanceToNextTopic) {
              setTimeout(() => {
                decision.advanceToNextTopic();
              }, 200);
            }
          },
          onAddMoreKeywords: () => {
            console.log('User wants to add more keywords');
            
            // Add message asking for more keywords
            if (setMessages) {
              setMessages(prev => [...prev, {
                role: 'AI',
                content: 'Great! Please type any additional keywords or domain areas you\'d like to include in your search.',
                id: `ai_keywords_add_more_${Date.now()}`
              }]);
            }
          }
        },
        disableFooter: false, // Allow chat for adding more keywords
        strategyPhase: StrategyPhase.SUGGESTIONS
      };
    } else {
      // No keywords found, use copilot-custom to get keyword suggestions
      console.log('🎯 KEYWORDS STRATEGY: No keywords found, requesting AI suggestions');
      
      // Update thinking accordion to show AI processing
      if (setMessages) {
        setMessages(prev => {
          const updatedMessages = prev.map(msg =>
            msg.role === 'AI' && msg.content === 'Typing...'
              ? {
                  ...msg,
                  thinking: `${msg.thinking || ''}\nNo keywords found in requirements. Requesting AI keyword suggestions...`
                }
              : msg
          );
          return updatedMessages;
        });
      }
      
      try {
        // Get role context for AI suggestions
        const roleContext = messages.length > 0 ? messages.slice(-5).map(msg => msg.content).join(' ') : 'Generic role';
        
        const strategyContext = {
          topic: 'keywords',
          context: `Keywords and domain expertise discovery. Role context: ${roleContext}. Please suggest relevant keywords, technical skills, domain areas, and industry expertise that would be important for this role.`,
          selectionType: 'keyword-suggestions',
          systemMessage: `You are a recruiter helping to identify relevant keywords and domain expertise for a job role. 

CRITICAL: You must return a JSON response in this EXACT format:
{
  "suggested_keywords": ["keyword1", "keyword2", "keyword3", "keyword4", "keyword5"],
  "reasoning": "Brief explanation of why these keywords are relevant"
}

Guidelines:
- Suggest 5-8 relevant keywords/skills/domain areas
- Include both technical and soft skills if applicable
- Consider industry-specific terminology
- Focus on searchable terms that candidates would have in their profiles
- Mix broad and specific terms (e.g., "JavaScript", "Frontend Development", "React")

Context: ${roleContext}`
        };

        const agentSelection = await handleAgentSelection(strategyContext);
        
        console.log('🎯 KEYWORDS STRATEGY: AI response:', agentSelection);
        
        // Parse AI suggested keywords
        let suggestedKeywords = [];
        let aiReasoning = '';
        
        if (agentSelection) {
          // Format 1: Direct suggested_keywords property
          if (agentSelection.suggested_keywords && Array.isArray(agentSelection.suggested_keywords)) {
            suggestedKeywords = agentSelection.suggested_keywords;
            aiReasoning = agentSelection.reasoning || 'AI suggested these keywords based on the role context';
          }
          // Format 2: Try parsing from selected_options
          else if (agentSelection.selected_options && Array.isArray(agentSelection.selected_options)) {
            suggestedKeywords = agentSelection.selected_options;
            aiReasoning = agentSelection.reasoning || 'AI suggested these keywords';
          }
          // Format 3: Try parsing raw response as JSON
          else if (typeof agentSelection === 'string') {
            try {
              const parsed = JSON.parse(agentSelection);
              if (parsed.suggested_keywords && Array.isArray(parsed.suggested_keywords)) {
                suggestedKeywords = parsed.suggested_keywords;
                aiReasoning = parsed.reasoning || 'AI suggested these keywords';
              }
            } catch (e) {
              console.warn('Could not parse AI response as JSON:', e);
            }
          }
        }
        
        if (suggestedKeywords.length > 0) {
          console.log('🎯 KEYWORDS STRATEGY: AI suggested keywords:', suggestedKeywords);
          
          // Update thinking accordion with AI suggestions
          if (setMessages) {
            setMessages(prev => {
              const updatedMessages = prev.map(msg =>
                msg.role === 'AI' && msg.content === 'Typing...'
                  ? {
                      ...msg,
                      thinking: `${msg.thinking || ''}\nAI suggested ${suggestedKeywords.length} keywords: ${suggestedKeywords.join(', ')}`
                    }
                  : msg
              );
              return updatedMessages;
            });
          }
          
          // Remove "Typing..." message
          if (setMessages) {
            setMessages(prev => prev.filter(msg => msg.content !== 'Typing...'));
          }
          
          // Show AI-suggested keywords for selection
          return {
            ...decision,
            actionType: 'RENDER_KEYWORDS_SELECTION',
            componentProps: {
              keywords: suggestedKeywords,
              extractedKeywords: [], // No extracted keywords
              userAddedKeywords: [], // No user-added keywords
              aiSuggestedKeywords: suggestedKeywords, // AI suggestions
              aiReasoning, // AI reasoning
              setMessages,
              advanceToNextTopic: decision.advanceToNextTopic,
              onSendText: decision.onSendText,
              messages,
              onKeywordsConfirmed: (confirmedKeywords) => {
                console.log('AI-suggested keywords confirmed:', confirmedKeywords);
                
                // Add confirmation message
                if (setMessages) {
                  setMessages(prev => [...prev, {
                    role: 'AI',
                    content: `Perfect! I've noted these keywords for your search: ${confirmedKeywords.join(', ')}. ${aiReasoning}`,
                    id: `ai_keywords_confirmed_${Date.now()}`
                  }]);
                }
                
                // Advance to next topic
                if (decision.advanceToNextTopic) {
                  setTimeout(() => {
                    decision.advanceToNextTopic();
                  }, 200);
                }
              },
              onAddMoreKeywords: () => {
                console.log('User wants to add more keywords to AI suggestions');
                
                // Add message asking for more keywords
                if (setMessages) {
                  setMessages(prev => [...prev, {
                    role: 'AI',
                    content: 'Great! Please type any additional keywords or domain areas you\'d like to include beyond the AI suggestions.',
                    id: `ai_keywords_add_more_${Date.now()}`
                  }]);
                }
              }
            },
            disableFooter: false, // Allow chat for adding more keywords
            strategyPhase: StrategyPhase.SUGGESTIONS
          };
        } else {
          console.log('🎯 KEYWORDS STRATEGY: AI could not provide keyword suggestions, falling back to manual input');
          
          // Update thinking accordion to show fallback
          if (setMessages) {
            setMessages(prev => {
              const updatedMessages = prev.map(msg =>
                msg.role === 'AI' && msg.content === 'Typing...'
                  ? {
                      ...msg,
                      thinking: `${msg.thinking || ''}\nAI could not suggest keywords, asking user for manual input...`
                    }
                  : msg
              );
              return updatedMessages;
            });
          }
          
          // Fallback to asking user manually
          return {
            ...decision,
            actionType: 'SEND_TO_BACKEND',
            agentGuidance: '🎯 I\'d like to understand what specific keywords, domain expertise, or industry areas are important for this role. Could you tell me about the key technical skills, tools, or domain knowledge you\'re looking for? For example: programming languages, frameworks, certifications, or industry-specific expertise.',
            strategyPhase: StrategyPhase.CONVERSATION
          };
        }
        
      } catch (aiError) {
        console.error('🎯 KEYWORDS STRATEGY: Error getting AI keyword suggestions:', aiError);
        
        // Update thinking accordion to show error
        if (setMessages) {
          setMessages(prev => {
            const updatedMessages = prev.map(msg =>
              msg.role === 'AI' && msg.content === 'Typing...'
                ? {
                    ...msg,
                    thinking: `${msg.thinking || ''}\nError getting AI suggestions, falling back to manual input...`
                  }
                : msg
            );
            return updatedMessages;
          });
        }
        
        // Fallback to manual input on AI error
        return {
          ...decision,
          actionType: 'SEND_TO_BACKEND',
          agentGuidance: '🎯 I\'d like to understand what specific keywords, domain expertise, or industry areas are important for this role. Could you tell me about the key technical skills, tools, or domain knowledge you\'re looking for? For example: programming languages, frameworks, certifications, or industry-specific expertise.',
          strategyPhase: StrategyPhase.CONVERSATION
        };
      }
    }
    
  } catch (error) {
    console.error('Error in keywords strategy:', error);
    
    // Fall back to regular conversation
    return {
      ...decision,
      actionType: 'SEND_TO_BACKEND',
      agentGuidance: 'Let\'s discuss the keywords and domain expertise requirements for this role. What specific areas or expertise are you looking for?',
      strategyPhase: StrategyPhase.CONVERSATION
    };
  }
};

export default keywordsStrategy;